import React, { createContext, useContext, useState, useEffect } from 'react';
import { getGoalsSettings, updateGoalsSettings, uploadGoalsSettingsIcon, uploadBadgeIcon as uploadBadgeIconApi, uploadStreakIcon as uploadStreakIconApi } from '../services/goalsPerformanceApi';
import { useAuth } from './AuthContext';
import toast from 'react-hot-toast';

const GoalsSettingsContext = createContext();

export const useGoalsSettings = () => {
  const context = useContext(GoalsSettingsContext);
  if (!context) {
    throw new Error('useGoalsSettings must be used within GoalsSettingsProvider');
  }
  return context;
};

export const GoalsSettingsProvider = ({ children }) => {
  const { token } = useAuth();
  const [loaded, setLoaded] = useState(false);
  const [savedSettings, setSavedSettings] = useState(null);
  // Default settings matching reference design
  const [settings, setSettings] = useState({
    // How the Goals module behaves: 'classic' | 'star' | 'score_only' | 'checklist'
    goalsMode: 'classic',
    // Star mode: all soft skills must be rated above this for the soft-skill star
    softSkillThreshold: 5,

    // Module Visibility
    myGoalsEnabled: false,
    myTasksEnabled: false,

    // Cycle Constraints
    cycleType: 'monthly', // 'monthly' or 'weekly'
    minGoals: 3,
    maxGoals: 7,
    criticalRequired: 1,

    // Goals weightage config (per-goal min/max; non-critical total must sum to 100, multiples of 10)
    minWeightage: 10,
    maxWeightage: 100,

    // Timing & Schedule
    submissionWindow: {
      startDay: 1,
      duration: 15
    },
    reviewWindow: {
      startDay: 16,
      duration: 14
    },
    evaluationWindow: {
      startDay: 1,
      duration: 30
    },
    allowWindowOverlap: false,
    icons: {
      submissionIcon: {
        s3Key: null,
        url: null
      },
      reviewIcon: {
        s3Key: null,
        url: null
      },
      evaluationIcon: {
        s3Key: null,
        url: null
      }
    },

    // Performance Workflow
    softSkillsEnabled: true,
    softSkills: [
      { id: 'ownership', label: 'Ownership', desc: 'Taking full accountability for deliverables' },
      { id: 'growth', label: 'Growth Attitude', desc: 'Willingness to learn and adapt to challenges' },
      { id: 'courage', label: 'Be Courageous', desc: 'Speaking up and taking bold initiatives' }
    ],

    // Recognition Rules
    badgeRanges: [
      { label: 'Best Employee', min: 8.5, max: 10, lessThan: false },
      { label: 'Growing Employee', min: 6, max: 8, lessThan: false },
      { label: 'Need to Improve', min: 0, max: 6, lessThan: true }
    ],
    streakPoints: {
      months: 1,
      points: 1,
      icon: {
        s3Key: null,
        url: null
      }
    },

    // Score from Achieved %
    scoreRanges: [
      { min: 0, max: 10, score: 1 },
      { min: 11, max: 20, score: 2 },
      { min: 21, max: 30, score: 3 },
      { min: 31, max: 40, score: 4 },
      { min: 41, max: 50, score: 5 },
      { min: 51, max: 60, score: 6 },
      { min: 61, max: 70, score: 7 },
      { min: 71, max: 80, score: 8 },
      { min: 81, max: 90, score: 9 },
      { min: 91, max: 100, score: 10 }
    ],

    // Engagement Alerts
    goalDeadlinesAlert: true,
    reviewRemindersAlert: true,
    badgeMilestonesAlert: false,
    reminderLeadTime: 3 // days before deadline expiry
  });

  // Load settings from backend when token is available
  useEffect(() => {
    if (!token) return;
    const loadSettings = async () => {
      try {
        const response = await getGoalsSettings();
        if (response.success && response.data) {
          const loadedSettings = response.data;
          if (loadedSettings.allowWindowOverlap === undefined) {
            loadedSettings.allowWindowOverlap = false;
          }
          if (!loadedSettings.goalsMode) loadedSettings.goalsMode = 'classic';
          if (loadedSettings.softSkillThreshold === undefined) loadedSettings.softSkillThreshold = 5;
          // Ensure scoreRanges exists, if not add default
          if (!loadedSettings.scoreRanges || loadedSettings.scoreRanges.length === 0) {
            loadedSettings.scoreRanges = [
              { min: 0, max: 10, score: 1 },
              { min: 11, max: 20, score: 2 },
              { min: 21, max: 30, score: 3 },
              { min: 31, max: 40, score: 4 },
              { min: 41, max: 50, score: 5 },
              { min: 51, max: 60, score: 6 },
              { min: 61, max: 70, score: 7 },
              { min: 71, max: 80, score: 8 },
              { min: 81, max: 90, score: 9 },
              { min: 91, max: 100, score: 10 }
            ];
          }
          setSettings(loadedSettings);
          setSavedSettings(JSON.parse(JSON.stringify(loadedSettings)));
        }
      } catch (error) {
        console.error('Error loading goals settings:', error);
        // Keep default settings on error
      } finally {
        setLoaded(true);
      }
    };

    loadSettings();
  }, [token]);

  const updateSettings = (updates) => {
    setSettings(prev => ({
      ...prev,
      ...updates
    }));
  };


  const updateSoftSkill = (id, updates) => {
    setSettings(prev => ({
      ...prev,
      softSkills: prev.softSkills.map(skill =>
        skill.id === id ? { ...skill, ...updates } : skill
      )
    }));
  };

  const addSoftSkill = () => {
    setSettings(prev => ({
      ...prev,
      softSkills: [
        ...prev.softSkills,
        {
          id: `skill-${Date.now()}`,
          label: 'New Metric',
          desc: 'Enter description here'
        }
      ]
    }));
  };

  const removeSoftSkill = (id) => {
    setSettings(prev => ({
      ...prev,
      softSkills: prev.softSkills.filter(skill => skill.id !== id)
    }));
  };

  const updateBadgeRange = (index, updates) => {
    setSettings(prev => ({
      ...prev,
      badgeRanges: prev.badgeRanges.map((range, i) =>
        i === index ? { ...range, ...updates } : range
      )
    }));
  };

  const updateScoreRange = (index, updates) => {
    setSettings(prev => ({
      ...prev,
      scoreRanges: prev.scoreRanges.map((range, i) =>
        i === index ? { ...range, ...updates } : range
      )
    }));
  };

  const saveSettings = async () => {
    // Comprehensive Validation
    const errors = [];

    // 2. Window Validation (Timing & Schedule)
    const validateWindow = (window, name, allowCrossMonth = false) => {
      if (window.startDay < 1 || window.startDay > 31) errors.push(`${name} start day must be between 1 and 31.`);
      const maxDuration = allowCrossMonth ? 45 : 31;
      if (window.duration < 1 || window.duration > maxDuration) errors.push(`${name} duration must be between 1 and ${maxDuration}.`);
      if (!allowCrossMonth && window.startDay + window.duration - 1 > 31) {
        errors.push(`${name} window expires on day ${window.startDay + window.duration - 1}, which exceeds the month limit (31).`);
      }
    };

    validateWindow(settings.submissionWindow, "Submission", true);
    validateWindow(settings.reviewWindow, "Review", true);
    validateWindow(settings.evaluationWindow, "Evaluation", true);

    // Sequential Windows Check only when overlapping is not allowed (existing behaviour when flag is false or missing)
    const allowOverlap = settings.allowWindowOverlap === true;
    if (!allowOverlap) {
      const submissionEndDay = settings.submissionWindow.startDay + settings.submissionWindow.duration - 1;
      const reviewStartDay = settings.reviewWindow.startDay;
      const reviewEndDay = settings.reviewWindow.startDay + settings.reviewWindow.duration - 1;
      const evaluationStartDay = settings.evaluationWindow.startDay;

      if (reviewStartDay <= submissionEndDay) {
        errors.push(`Review window (Start: Day ${reviewStartDay}) must start AFTER the submission window ends (End: Day ${submissionEndDay}).`);
      }
      if (evaluationStartDay <= reviewEndDay) {
        errors.push(`Evaluation window (Start: Day ${evaluationStartDay}) must start AFTER the review window ends (End: Day ${reviewEndDay}).`);
      }
    }

    // 3. Score Ranges (Contiguous and non-overlapping)
    if (settings.scoreRanges && settings.scoreRanges.length > 0) {
      let lastMax = -1;
      settings.scoreRanges.forEach((range, index) => {
        if (range.min >= range.max) errors.push(`Score Range ${index + 1}: Min (${range.min}%) must be less than Max (${range.max}%).`);
        if (range.min <= lastMax) errors.push(`Score Range ${index + 1}: Range start (${range.min}%) overlaps with previous range (End: ${lastMax}%).`);
        lastMax = range.max;
      });
      if (lastMax > 100) errors.push("Score ranges cannot exceed 100%.");
    }

    // 4. Badge Ranges
    if (settings.badgeRanges && settings.badgeRanges.length > 0) {
      settings.badgeRanges.forEach((range, index) => {
        if (!range.lessThan && range.min >= range.max) {
          errors.push(`Badge Range "${range.label}": Min (${range.min}) must be less than Max (${range.max}).`);
        }
      });
    }

    // 5. Soft Skills
    if (settings.softSkillsEnabled) {
      if (!settings.softSkills || settings.softSkills.length === 0) {
        errors.push("At least one soft skill metric is required when enabled.");
      } else {
        settings.softSkills.forEach((skill, index) => {
          if (!skill.label || skill.label.trim() === "") {
            errors.push(`Soft Skill ${index + 1}: Metric Title is required.`);
          }
        });
      }
    }

    if (errors.length > 0) {
      errors.forEach(err => toast.error(err, { duration: 5000 }));
      return null;
    }

    try {
      const response = await updateGoalsSettings(settings);
      if (response.success) {
        toast.success('Settings saved successfully');
        setSavedSettings(JSON.parse(JSON.stringify(settings)));
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving goals settings:', error);
      toast.error(error.response?.data?.error?.message || 'Failed to save settings');
      throw error;
    }
  };

  const uploadIcon = async (periodType, iconFile) => {
    try {
      const response = await uploadGoalsSettingsIcon(periodType, iconFile);
      if (response.success) {
        // Update settings with new icon
        const iconFieldMap = {
          'submission': 'submissionIcon',
          'review': 'reviewIcon',
          'evaluation': 'evaluationIcon'
        };
        const iconField = iconFieldMap[periodType];

        setSettings(prev => ({
          ...prev,
          icons: {
            ...prev.icons,
            [iconField]: {
              s3Key: response.data.s3Key,
              url: response.data.url
            }
          }
        }));

        toast.success('Icon uploaded successfully');
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to upload icon');
      }
    } catch (error) {
      console.error('Error uploading icon:', error);
      toast.error(error.response?.data?.error?.message || 'Failed to upload icon');
      throw error;
    }
  };

  const uploadBadgeIcon = async (badgeIndex, iconFile) => {
    try {
      const response = await uploadBadgeIconApi(badgeIndex, iconFile);
      if (response.success) {
        // Update settings with new badge icon
        setSettings(prev => ({
          ...prev,
          badgeRanges: prev.badgeRanges.map((range, index) =>
            index === badgeIndex
              ? { ...range, icon: { s3Key: response.data.s3Key, url: response.data.url } }
              : range
          )
        }));

        toast.success('Badge icon uploaded successfully');
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to upload badge icon');
      }
    } catch (error) {
      console.error('Error uploading badge icon:', error);
      toast.error(error.response?.data?.error?.message || 'Failed to upload badge icon');
      throw error;
    }
  };

  const uploadStreakIcon = async (iconFile) => {
    try {
      const response = await uploadStreakIconApi(iconFile);
      if (response.success) {
        // Update settings with new streak icon
        setSettings(prev => ({
          ...prev,
          streakPoints: {
            ...prev.streakPoints,
            icon: { s3Key: response.data.s3Key, url: response.data.url }
          }
        }));

        toast.success('Streak icon uploaded successfully');
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to upload streak icon');
      }
    } catch (error) {
      console.error('Error uploading streak icon:', error);
      toast.error(error.response?.data?.error?.message || 'Failed to upload streak icon');
      throw error;
    }
  };

  const removeIcon = (periodType) => {
    const iconFieldMap = {
      'submission': 'submissionIcon',
      'review': 'reviewIcon',
      'evaluation': 'evaluationIcon'
    };
    const iconField = iconFieldMap[periodType];

    setSettings(prev => ({
      ...prev,
      icons: {
        ...prev.icons,
        [iconField]: { s3Key: null, url: null }
      }
    }));
    toast.success('Icon removed successfully');
  };

  const removeBadgeIcon = (badgeIndex) => {
    setSettings(prev => ({
      ...prev,
      badgeRanges: prev.badgeRanges.map((range, index) =>
        index === badgeIndex
          ? { ...range, icon: { s3Key: null, url: null } }
          : range
      )
    }));
    toast.success('Badge icon removed successfully');
  };

  const removeStreakIcon = () => {
    setSettings(prev => ({
      ...prev,
      streakPoints: {
        ...prev.streakPoints,
        icon: { s3Key: null, url: null }
      }
    }));
    toast.success('Streak icon removed successfully');
  };

  const hasUnsavedChanges = loaded && savedSettings !== null &&
    JSON.stringify(settings) !== JSON.stringify(savedSettings);

  const resetToSaved = () => {
    if (savedSettings) {
      setSettings(JSON.parse(JSON.stringify(savedSettings)));
    }
  };

  return (
    <GoalsSettingsContext.Provider
      value={{
        settings,
        loaded,
        hasUnsavedChanges,
        resetToSaved,
        updateSettings,
        updateSoftSkill,
        addSoftSkill,
        removeSoftSkill,
        updateBadgeRange,
        updateScoreRange,
        saveSettings,
        uploadIcon,
        uploadBadgeIcon,
        uploadStreakIcon,
        removeIcon,
        removeBadgeIcon,
        removeStreakIcon
      }}
    >
      {children}
    </GoalsSettingsContext.Provider>
  );
};
